
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, Monitor, Download, Home, Globe } from 'lucide-react';
import { useEffect, useState } from 'react';

const InstallGuidePage = () => {
  const [deviceType, setDeviceType] = useState<'ios' | 'android' | 'desktop'>('ios');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      setDeviceType('ios');
    } else if (userAgent.includes('android')) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  const handleUseBrowser = () => {
    // Set a flag to bypass PWA detection and go straight to app
    localStorage.setItem('gambgo_use_browser_mode', 'true');
    // Redirect to splash screen to enter the app
    window.location.href = '/?source=browser';
  };

  const renderIOSInstructions = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-blue-900 mb-6 flex items-center gap-2">
          <Smartphone className="w-6 h-6" />
          Install on iPhone/iPad
        </h3>
        
        <div className="text-center space-y-6">
          <div>
            <p className="text-lg font-medium text-blue-900 mb-2">Download from the App Store</p>
            <p className="text-sm text-gray-600 mb-6">Get the official GAMBGO app for the best experience</p>
          </div>
          
          <a 
            href="https://apps.apple.com/app/gambgo/id6761062098"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <div className="bg-black hover:bg-gray-800 transition-colors duration-200 rounded-xl px-8 py-4 flex items-center gap-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="text-xs text-gray-300 font-medium">Download on the</div>
                <div className="text-xl font-bold text-white">App Store</div>
              </div>
            </div>
          </a>
          
          <div className="bg-blue-100 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-800">
              <strong>✓ Official App:</strong> Get push notifications, offline support, and the smoothest experience
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAndroidInstructions = () => (
    <div className="space-y-6">
      <div className="bg-green-50 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-green-900 mb-6 flex items-center gap-2">
          <Smartphone className="w-6 h-6" />
          Install on Android
        </h3>
        
        <div className="text-center space-y-6">
          <div>
            <p className="text-lg font-medium text-green-900 mb-2">Download from Google Play Store</p>
            <p className="text-sm text-gray-600 mb-6">Get the official GAMBGO app for the best experience</p>
          </div>
          
          <a 
            href="https://play.google.com/store/apps/details?id=co.median.android.wzaewp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <div className="bg-black hover:bg-gray-800 transition-colors duration-200 rounded-xl px-8 py-4 flex items-center gap-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="text-xs text-gray-300 font-medium">GET IT ON</div>
                <div className="text-xl font-bold text-white">Google Play</div>
              </div>
            </div>
          </a>
          
          <div className="bg-green-100 rounded-lg p-4 mt-6">
            <p className="text-sm text-green-800">
              <strong>✓ Official App:</strong> Get push notifications, offline support, and the smoothest experience
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDesktopInstructions = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <Monitor className="w-6 h-6" />
          Install on Desktop
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
            <div>
              <p className="font-medium">Look for the install icon</p>
              <div className="flex items-center gap-2 mt-1">
                <Download className="w-4 h-4 text-purple-600" />
                <p className="text-sm text-gray-600">In your browser's address bar</p>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
            <div>
              <p className="font-medium">Click "Install GAMBGO"</p>
              <p className="text-sm text-gray-600">Or use browser menu → "Install GAMBGO"</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
            <div>
              <p className="font-medium">Launch from desktop</p>
              <p className="text-sm text-gray-600">GAMBGO will appear as a desktop app</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-gray-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <img 
              src="/lovable-uploads/40013902-21dc-443e-b648-06fc550f98e5.png" 
              alt="GAMBGO Logo"
              className="w-8 h-8"
            />
            <span className="text-lg font-bold text-gray-900">GAMBGO</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-24 h-24 mx-auto mb-6">
            <img 
              src="/lovable-uploads/40013902-21dc-443e-b648-06fc550f98e5.png" 
              alt="GAMBGO Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Install GAMBGO
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Follow these simple steps to install GAMBGO on your device. Once installed, the app will automatically open when you visit this page.
          </p>
        </div>

        {/* Device Type Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            <button
              onClick={() => setDeviceType('ios')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                deviceType === 'ios' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              iPhone/iPad
            </button>
            <button
              onClick={() => setDeviceType('android')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                deviceType === 'android' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Android
            </button>
            <button
              onClick={() => setDeviceType('desktop')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                deviceType === 'desktop' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Desktop
            </button>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="mb-12">
          {deviceType === 'ios' && renderIOSInstructions()}
          {deviceType === 'android' && renderAndroidInstructions()}
          {deviceType === 'desktop' && renderDesktopInstructions()}
        </div>

        {/* Back to Home and Use Browser buttons */}
        <div className="text-center space-y-4">
          <div>
            <Button 
              onClick={handleUseBrowser}
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold rounded-xl mr-4"
            >
              <Globe className="w-5 h-5 mr-2" />
              Use Browser Instead
            </Button>
          </div>
          <div>
            <Link to="/">
              <Button 
                variant="outline" 
                size="lg" 
                className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3 text-lg font-semibold rounded-xl"
              >
                Back to Home
              </Button>
            </Link>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Home className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Auto-Launch</h3>
            <p className="text-gray-600">Once installed, GAMBGO will automatically open when you visit our website</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Download className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Offline Support</h3>
            <p className="text-gray-600">Browse your order history and favorites even when offline</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Native Experience</h3>
            <p className="text-gray-600">Enjoy a smooth, app-like experience with push notifications</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallGuidePage;
